import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useGame } from '../context/GameContext';
import { GameService } from '../services/gameService';
import { getMembership } from '../services/billingService';
import {
  SCENE_CATALOG,
  canUseScene,
  getScene,
  isVisibleGalleryArtwork
} from '../config/membership';
import useWorkspace from '../hooks/useWorkspace';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Modal from '../components/ui/Modal';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { formatTime } from '../utils/formatters';
import {
  canvasToPngBlob,
  getPainterlyStage,
  renderPainterlyFrame,
} from '../utils/painterlyRenderer';
import PaintingCanvas from '../components/ui/PaintingCanvas';

const { FiClock, FiPlay, FiSquare, FiImage, FiEye, FiCheck, FiPause, FiDownload, FiLock, FiFolder } = FiIcons;

const focusLengths = [15, 25, 45, 60];
const DEFAULT_SCENE = SCENE_CATALOG[0].id;
const scenes = SCENE_CATALOG.map((scene) => ({
  id: scene.id, label: scene.name, tier: scene.tier
}));

const FOCUS_TASK_KEY = 'mindgarden-focus-task-v1';
const COLLECTIONS_KEY = 'mindgarden-art-collections-v1';

function readStoredValue(key, fallback) {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

function getSceneName(sceneId) {
  return getScene(sceneId)?.name
    || String(sceneId || 'Artwork').replaceAll('-', ' ');
}

function isImageScene(sceneId) {
  return getScene(sceneId)?.type === 'image';
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read artwork image."));
    reader.readAsDataURL(blob);
  });
}

async function inlineCatalogImage(svg, scene) {
  const images = Array.from(svg.querySelectorAll('image'));
  if (!images.length) return;

  const response = await fetch(scene.source, {
    mode: 'cors',
    credentials: 'omit',
    cache: 'force-cache'
  });

  if (!response.ok) {
    throw new Error(`Artwork source returned ${response.status}.`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.startsWith('image/')) {
    throw new Error("Artwork source did not return image data.");
  }

  const dataUrl = await blobToDataUrl(await response.blob());
  images.forEach((image) => {
    image.setAttribute('href', dataUrl);
    image.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', dataUrl);
  });
}

function getNearestFocusLength(minutes) {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return null;

  return focusLengths.reduce((nearest, length) => (
    Math.abs(length - value) < Math.abs(nearest - value) ? length : nearest
  ), focusLengths[0]);
}

export default function FocusSession() {
  const { state, actions } = useGame();
  const { tasks, updateTask, storageError } = useWorkspace();
  const [searchParams] = useSearchParams();
  const requestedTaskId = searchParams.get('task');
  const requestedTask = tasks.find((task) => task.id === requestedTaskId) || null;
  const visibleGallery = (state.gallery || []).filter(isVisibleGalleryArtwork);
  const visibleGalleryIds = new Set(visibleGallery.map((art) => art.id));
  const [selectedScene, setSelectedScene] = useState(DEFAULT_SCENE);
  const [intention, setIntention] = useState('');
  const [demoActive, setDemoActive] = useState(false);
  const [demoProgress, setDemoProgress] = useState(0);
  const [demoPaused, setDemoPaused] = useState(false);
  const [demoRun, setDemoRun] = useState(0);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [membership, setMembership] = useState({ plan: 'free', verified: false });
  const [selectedArt, setSelectedArt] = useState(null);
  const [collectionArt, setCollectionArt] = useState(null);
  const [collectionModalOpen, setCollectionModalOpen] = useState(false);
  const [collections, setCollections] = useState(() => {
    const stored = readStoredValue(COLLECTIONS_KEY, { version: 1, items: [] });
    return Array.isArray(stored?.items) ? stored.items : [];
  });
  const [feedback, setFeedback] = useState('');
  const intentionRef = useRef(null);
  const collectionNameRef = useRef(null);
  const canvasWrapperRef = useRef(null);
  const canvasScrollFrameRef = useRef(null);
  const previousFocusStatus = useRef(state.focus.status);
  const suggestedTaskRef = useRef(null);
  const legacyMetadataCheckedRef = useRef(false);
  const markedTaskRef = useRef(null);
  const demoStartedAtRef = useRef(null);
  const demoPausedElapsedRef = useRef(0);
  const previewPreviousSceneRef = useRef(DEFAULT_SCENE);
  const exportUrlRef = useRef(null);

  const [exportError, setExportError] = useState(null);
  const [exportOriginalUrl, setExportOriginalUrl] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [readyExport, setReadyExport] = useState(null);

  const isActive = state.focus.status === 'active';
  const isPaused = state.focus.status === 'paused';
  const isCompleted = state.focus.status === 'completed';
  const sessionVisible = isActive || isPaused || isCompleted;
  const focusDurationMs = state.focus.durationMs || state.focus.length * 60 * 1000;
  const progress = isActive || isPaused
    ? Math.max(0, Math.min(1, (state.focus.elapsed || 0) / focusDurationMs))
    : (isCompleted ? 1 : 0);

  const expectedReward = GameService.calculateFocusReward(state.focus.length, state.compost);
  const verifiedPlus = membership.verified === true && membership.plan === 'plus';

  const scrollCanvasIntoView = () => {
    if (canvasScrollFrameRef.current) {
      cancelAnimationFrame(canvasScrollFrameRef.current);
    }

    canvasScrollFrameRef.current = requestAnimationFrame(() => {
      canvasScrollFrameRef.current = null;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      canvasWrapperRef.current?.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    });
  };

  useEffect(() => {
    let mounted = true;

    getMembership().then((result) => {
      if (mounted && result.ok) setMembership(result.membership);
    });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (sessionVisible && state.focus.scene) {
      setSelectedScene(state.focus.scene);
    } else if (!sessionVisible) {
      setSelectedScene((current) => SCENE_CATALOG.some((scene) => scene.id === current)
        ? current : DEFAULT_SCENE);
    }
  }, [sessionVisible, state.focus.scene]);

  useEffect(() => {
    if (
      state.focus.status !== 'idle'
      || !requestedTask
      || suggestedTaskRef.current === requestedTask.id
    ) {
      return;
    }

    suggestedTaskRef.current = requestedTask.id;
    const suggestedLength = getNearestFocusLength(requestedTask.estimatedMinutes);
    if (suggestedLength) actions.setFocusLength(suggestedLength);
  }, [actions, requestedTask, state.focus.status]);

  useEffect(() => {
    previousFocusStatus.current = state.focus.status;

    if (
      legacyMetadataCheckedRef.current
      || !['active', 'paused'].includes(state.focus.status)
      || state.focus.taskId
    ) {
      return;
    }

    legacyMetadataCheckedRef.current = true;
    const metadata = readStoredValue(FOCUS_TASK_KEY, null);

    if (
      metadata?.taskId
      && (!metadata.sessionId || metadata.sessionId === state.focus.sessionId)
      && typeof actions.attachFocusMetadata === 'function'
    ) {
      actions.attachFocusMetadata({
        sessionId: state.focus.sessionId,
        taskId: metadata.taskId,
        taskTitle: metadata.taskTitle || '',
        taskProject: metadata.taskProject || ''
      });
    }

    try {
      localStorage.removeItem(FOCUS_TASK_KEY);
    } catch {
      setFeedback('Task details could not be saved with this session.');
    }
  }, [
    actions,
    state.focus.sessionId,
    state.focus.status,
    state.focus.taskId
  ]);

  useEffect(() => {
    let frame;

    if (!demoActive) {
      demoStartedAtRef.current = null;
      demoPausedElapsedRef.current = 0;
      setDemoProgress(0);
      return undefined;
    }

    if (demoPaused) return undefined;

    demoStartedAtRef.current = Date.now() - demoPausedElapsedRef.current;
    const duration = 30000;
    const animate = () => {
      const now = Date.now();
      const elapsed = now - demoStartedAtRef.current;
      if (elapsed < duration) {
        demoPausedElapsedRef.current = elapsed;
        setDemoProgress(elapsed / duration);
        frame = requestAnimationFrame(animate);
      } else {
        demoPausedElapsedRef.current = duration;
        setDemoProgress(1);
      }
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [demoActive, demoPaused, demoRun]);

  useEffect(() => () => {
    if (canvasScrollFrameRef.current) {
      cancelAnimationFrame(canvasScrollFrameRef.current);
    }
    if (exportUrlRef.current) URL.revokeObjectURL(exportUrlRef.current);
  }, []);

  const clearReadyExport = () => {
    if (exportUrlRef.current) {
      URL.revokeObjectURL(exportUrlRef.current);
      exportUrlRef.current = null;
    }
    setReadyExport(null);
    setExportOriginalUrl(null);
  };

  const handleStart = () => {
    if (!canUseScene(verifiedPlus ? 'plus' : 'free', selectedScene)) {
      setFeedback('Choose a Free scene or explore Studio Plus.');
      return;
    }

    const seed = Math.floor(Math.random() * 100000);
    const intentText = intentionRef.current ? intentionRef.current.value : intention;

    actions.startFocus(state.focus.length, selectedScene, seed, intentText, {
      taskId: requestedTask?.id || null,
      taskTitle: requestedTask?.title || '',
      taskProject: requestedTask?.project || ''
    });
    scrollCanvasIntoView();
  };

  const handleEndEarly = () => {
    setShowEndConfirm(true);
  };

  const confirmEndEarly = () => {
    setShowEndConfirm(false);
    actions.stopFocus();
  };

  const handleDownload = async (art, highResolution = false, sourceId = `canvas-${art.id || 'demo'}`) => {
    if (exportingId) return;

    if (highResolution && !verifiedPlus) {
      setExportError('High-resolution export requires a verified Studio Plus membership.');
      return;
    }

    const sceneDetails = getScene(art.scene);
    const imageArtwork = sceneDetails?.type === 'image';
    const width = highResolution ? 3200 : 1600;
    const height = highResolution ? 2400 : 1200;
    const exportWidth = imageArtwork ? sceneDetails.width : width;
    const exportHeight = imageArtwork ? sceneDetails.height : height;
    let svgUrl;
    let timeoutId;

    clearReadyExport();
    setExportingId(`${art.id}-${highResolution ? 'high' : 'standard'}`);
    setExportError(null);
    try {
      if (imageArtwork) {
        const paintedCanvas = await renderPainterlyFrame({
          source: sceneDetails.source,
          artSeed: art.artSeed,
          progress: 1,
          width: exportWidth,
          height: exportHeight
        });
        const paintedBlob = await canvasToPngBlob(paintedCanvas);
        const paintedPngUrl = URL.createObjectURL(paintedBlob);
        exportUrlRef.current = paintedPngUrl;
        setReadyExport({
          artId: art.id,
          url: paintedPngUrl,
          filename: `mindgarden-${art.scene}${art.sample ? '-sample' : ''}.png`,
          label: art.sample ? 'Save painting preview' : 'Save PNG'
        });
        setFeedback('Your PNG is ready. Use the Save PNG link to download it.');
        return;
      }

      const svgEl = document.getElementById(sourceId);
      if (!svgEl) throw new Error("Artwork canvas not found.");
      if (!(svgEl instanceof SVGSVGElement)) throw new Error("Artwork canvas is not a valid SVG.");

      const clone = svgEl.cloneNode(true);
      clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
      clone.setAttribute("width", String(width));
      clone.setAttribute("height", String(height));
      clone.setAttribute("viewBox", "0 0 400 300");
      clone.removeAttribute("class");
      clone.removeAttribute("style");

      if (imageArtwork) {
        clone.setAttribute("width", String(exportWidth));
        clone.setAttribute("height", String(exportHeight));
        clone.setAttribute("viewBox", "0 0 1024 1024");
        await inlineCatalogImage(clone, sceneDetails);
      }

      const svgData = new XMLSerializer().serializeToString(clone);
      const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
      svgUrl = URL.createObjectURL(svgBlob);

      const img = new Image();

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error("Failed to decode artwork image."));
        timeoutId = window.setTimeout(
          () => reject(new Error("Artwork export timed out. Please try again.")),
          12000
        );
        img.src = svgUrl;
      });
      window.clearTimeout(timeoutId);

      if (typeof img.decode === 'function') {
        await Promise.race([
          img.decode().catch(() => undefined),
          new Promise((resolve) => window.setTimeout(resolve, 3000))
        ]);
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      if (imageArtwork) {
        canvas.width = exportWidth;
        canvas.height = exportHeight;
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Could not initialize drawing context.");

      ctx.fillStyle = "#F6F3EC";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("Failed to generate PNG blob.");

      const pngUrl = URL.createObjectURL(blob);
      exportUrlRef.current = pngUrl;
      setReadyExport({
        artId: art.id,
        url: pngUrl,
        filename: `mindgarden-${art.scene}${highResolution ? '-high-resolution' : ''}.png`,
        label: highResolution ? 'Save high-resolution PNG' : 'Save PNG'
      });
      setFeedback('Your PNG is ready. Use the Save PNG link to download it.');
    } catch (err) {
      console.error("Export failed:", err);
      setExportError(err.message || "Failed to export artwork.");
      if (imageArtwork) setExportOriginalUrl(sceneDetails.source);
    } finally {
      window.clearTimeout(timeoutId);
      if (svgUrl) URL.revokeObjectURL(svgUrl);
      setExportingId(null);
    }
  };

  const handleSceneSelect = (scene) => {
    if (!canUseScene(verifiedPlus ? 'plus' : 'free', scene.id)) {
      setFeedback(`${scene.label} is part of Studio Plus. You can preview it before deciding.`);
      return;
    }

    setSelectedScene(scene.id);
    setFeedback('');
  };

  const startPreview = (sceneId) => {
    clearReadyExport();
    previewPreviousSceneRef.current = selectedScene;
    setSelectedScene(sceneId);
    demoStartedAtRef.current = null;
    demoPausedElapsedRef.current = 0;
    setDemoProgress(0);
    setDemoPaused(false);
    setDemoRun((value) => value + 1);
    setDemoActive(true);
    scrollCanvasIntoView();
  };

  const replayPreview = () => {
    clearReadyExport();
    demoStartedAtRef.current = null;
    demoPausedElapsedRef.current = 0;
    setDemoProgress(0);
    setDemoPaused(false);
    setDemoRun((value) => value + 1);
    scrollCanvasIntoView();
  };

  const togglePreviewPause = () => {
    if (!demoPaused && demoStartedAtRef.current) {
      demoPausedElapsedRef.current = Math.min(
        30000,
        Date.now() - demoStartedAtRef.current
      );
    }
    setDemoPaused((value) => !value);
  };

  const closePreview = () => {
    clearReadyExport();
    const previousScene = previewPreviousSceneRef.current;
    setSelectedScene(
      canUseScene(verifiedPlus ? 'plus' : 'free', previousScene)
        ? previousScene
        : DEFAULT_SCENE
    );
    setDemoActive(false);
    setDemoPaused(false);
  };

  const saveCollections = (nextCollections) => {
    try {
      localStorage.setItem(COLLECTIONS_KEY, JSON.stringify({
        version: 1,
        items: nextCollections
      }));
      setCollections(nextCollections);
      setFeedback('');
    } catch {
      setFeedback('This collection could not be saved on this device.');
    }
  };

  const openCollectionModal = (art = null) => {
    if (!verifiedPlus) {
      setFeedback('Creating collections requires a verified Studio Plus membership.');
      return;
    }

    setCollectionArt(art);
    setCollectionModalOpen(true);
  };

  const closeCollectionModal = () => {
    setCollectionModalOpen(false);
    setCollectionArt(null);
  };

  const createCollection = (event) => {
    event.preventDefault();

    if (!verifiedPlus) {
      setFeedback('Creating collections requires a verified Studio Plus membership.');
      return;
    }

    const name = collectionNameRef.current?.value.trim();
    if (!name) {
      setFeedback('Enter a collection name.');
      collectionNameRef.current?.focus();
      return;
    }

    saveCollections([
      ...collections,
      {
        id: `collection-${Date.now()}`,
        name,
        artIds: collectionArt?.id ? [collectionArt.id] : []
      }
    ]);
    collectionNameRef.current.value = '';
    closeCollectionModal();
  };

  const addToCollection = (collectionId, artId) => {
    if (!verifiedPlus) {
      setFeedback('Editing collections requires a verified Studio Plus membership.');
      return;
    }

    saveCollections(collections.map((collection) => (
      collection.id === collectionId
        ? {
          ...collection,
          artIds: Array.from(new Set([...(collection.artIds || []), artId]))
        }
        : collection
    )));
    closeCollectionModal();
    setFeedback('Artwork added to collection.');
  };

  const markTaskComplete = () => {
    const taskId = state.focus.taskId;
    const currentTask = tasks.find((task) => task.id === taskId);

    if (!taskId || currentTask?.status === 'completed' || markedTaskRef.current === taskId) return;

    markedTaskRef.current = taskId;
    updateTask(taskId, { status: 'completed' });
    setFeedback('Task marked complete.');
  };

  const activeScene = isActive || isPaused || isCompleted ? state.focus.scene : selectedScene;
  const activeSeed = isActive || isPaused || isCompleted ? state.focus.artSeed : 12345;
  const displayProgress = demoActive ? demoProgress : progress;
  const isControlsDisabled = isActive || isPaused || isCompleted || demoActive;
  const painterlyStage = isImageScene(activeScene)
    ? getPainterlyStage(displayProgress)
    : null;
  const canSavePreview = (
    demoActive
    && demoProgress >= 1
    && isImageScene(activeScene)
    && canUseScene('free', activeScene)
  );
  const completedTask = tasks.find((task) => task.id === state.focus.taskId)
    || (state.focus.taskTitle
      ? {
        id: state.focus.taskId,
        title: state.focus.taskTitle,
        project: state.focus.taskProject,
        status: markedTaskRef.current === state.focus.taskId ? 'completed' : 'todo'
      }
      : null);

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {!sessionVisible && (
        <div className="text-center mb-8">
          <h1 className="text-4xl lg:text-5xl font-cormorant font-bold text-pine mb-3">Make time. Make something beautiful.</h1>
          <p className="text-pine/70 font-dm-sans max-w-xl mx-auto">
            Choose a scene. As you focus, layers of painted strokes build it from a loose sketch to a finished painting.
          </p>
        </div>
      )}

      {(feedback || storageError) && (
        <div role="status" className="rounded-xl border border-goldenrod/20 bg-goldenrod/10 px-4 py-3 text-sm text-pine">
          {feedback || storageError}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div
          ref={canvasWrapperRef}
          className={sessionVisible ? 'lg:col-span-12 max-w-4xl mx-auto w-full' : 'lg:col-span-8'}
          style={{ scrollMarginTop: '5rem' }}
        >
          <Card className="p-4 bg-white shadow-xl flex flex-col items-center justify-center relative min-h-[400px]">
            <div
              className="w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-inner border border-pine/10 relative bg-paper"
              style={isImageScene(activeScene) ? { aspectRatio: '1 / 1', maxWidth: '42rem' } : undefined}
            >
              <PaintingCanvas
                id={`canvas-${(isActive || isPaused) ? state.focus.sessionId : 'demo'}`}
                scene={activeScene}
                progress={displayProgress}
                artSeed={activeSeed}
                className="w-full h-full"
              />

              <AnimatePresence>
                {(isActive || isPaused) && !demoActive && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute top-4 right-4 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full shadow-sm border border-pine/10 flex items-center gap-2"
                  >
                    {isPaused ? (
                      <SafeIcon icon={FiPause} className="text-goldenrod animate-pulse" />
                    ) : (
                      <div className="w-2 h-2 rounded-full bg-sage animate-pulse" />
                    )}
                    <span className="font-mono text-pine font-bold tracking-wider">
                      {formatTime(state.focus.remaining)}
                    </span>
                  </motion.div>
                )}
                {demoActive && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute top-4 right-4 bg-pine/90 text-white backdrop-blur-md px-4 py-2 rounded-full shadow-sm text-xs font-bold uppercase tracking-widest"
                  >
                    Preview Mode (30s)
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {painterlyStage && (
              <p className="mt-4 text-sm font-bold text-pine/70" aria-live="polite">
                {painterlyStage}
              </p>
            )}

            <div className="mt-8 flex items-center justify-center gap-4 w-full px-4">
              {demoActive ? (
                <div className="text-center w-full">
                  {demoProgress >= 1 ? (
                    <div className="space-y-4">
                      <div className="inline-flex items-center gap-2 text-sage font-bold text-xl bg-sage/10 px-6 py-3 rounded-2xl">
                        Preview Complete
                      </div>
                      <div className="flex gap-4 justify-center">
                        {canSavePreview && (
                          <Button
                            variant="secondary"
                            onClick={() => handleDownload({
                              id: 'preview',
                              scene: activeScene,
                              artSeed: activeSeed,
                              sample: true
                            })}
                            icon={FiDownload}
                            disabled={exportingId === 'preview-standard'}
                          >
                            {exportingId === 'preview-standard' ? 'Preparing...' : 'Save painting preview'}
                          </Button>
                        )}
                        <Button variant="secondary" onClick={replayPreview}>
                          Replay
                        </Button>
                        <Button variant="primary" onClick={closePreview}>
                          Close
                        </Button>
                      </div>
                      {readyExport?.artId === 'preview' && (
                        <div className="flex flex-wrap justify-center gap-3">
                          <a
                            href={readyExport.url}
                            download={readyExport.filename}
                            className="min-h-11 rounded-xl bg-pine px-5 py-2 inline-flex items-center justify-center text-sm font-bold text-paper focus:outline-none focus:ring-2 focus:ring-pine focus:ring-offset-2"
                          >
                            Save PNG
                          </a>
                          <a
                            href={readyExport.url}
                            target="_blank"
                            rel="noreferrer"
                            className="min-h-11 rounded-xl border border-pine/10 bg-white px-5 py-2 inline-flex items-center justify-center text-sm font-bold text-pine focus:outline-none focus:ring-2 focus:ring-pine"
                          >
                            Open PNG
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-wrap justify-center gap-3">
                      <Button variant="secondary" onClick={togglePreviewPause} icon={demoPaused ? FiPlay : FiPause}>
                        {demoPaused ? 'Resume Preview' : 'Pause Preview'}
                      </Button>
                      <Button variant="secondary" onClick={closePreview} className="text-crimson">
                        Cancel Preview
                      </Button>
                    </div>
                  )}
                </div>
              ) : isCompleted ? (
                <div className="text-center w-full">
                  <div className="inline-flex items-center gap-2 text-sage font-bold text-xl mb-4 bg-sage/10 px-6 py-3 rounded-2xl">
                    <SafeIcon icon={FiCheck} /> Session Complete
                  </div>
                  <p className="font-bold text-pine">{getSceneName(state.focus.scene)}</p>
                  {completedTask && (
                    <div className="mb-4">
                      <p className="font-bold text-pine">{completedTask.title}</p>
                      {completedTask.project && (
                        <p className="text-sm text-pine/60">{completedTask.project}</p>
                      )}
                      <p className="text-sm text-pine/60">{state.focus.length} minutes focused</p>
                    </div>
                  )}
                  {!completedTask && (
                    <p className="text-sm text-pine/60 mb-4">{state.focus.length} minutes focused</p>
                  )}
                  {completedTask && completedTask.status !== 'completed' && (
                    <Button variant="secondary" onClick={markTaskComplete} className="w-full max-w-xs mx-auto mb-3" icon={FiCheck}>
                      Mark task complete
                    </Button>
                  )}
                  <Button variant="primary" onClick={() => actions.stopFocus()} className="w-full max-w-xs mx-auto">
                    Save & Return to Studio
                  </Button>
                </div>
              ) : (isActive || isPaused) ? (
                <div className="w-full">
                  {state.focus.taskTitle && (
                    <div className="mb-4 text-center">
                      <p className="font-bold text-pine">{state.focus.taskTitle}</p>
                      {state.focus.taskProject && (
                        <p className="text-sm text-pine/60">{state.focus.taskProject}</p>
                      )}
                    </div>
                  )}
                  {!state.focus.taskTitle && state.focus.intention && (
                    <p className="font-bold text-pine text-center mb-4">{state.focus.intention}</p>
                  )}
                  <div
                    role="progressbar"
                    aria-label="Focus session progress"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    aria-valuenow={Math.round(progress * 100)}
                    className="h-2 max-w-lg mx-auto mb-5 overflow-hidden rounded-full bg-pine/10"
                  >
                    <div
                      className="h-full rounded-full bg-sage transition-[width] motion-reduce:transition-none"
                      style={{ width: `${progress * 100}%` }}
                    />
                  </div>
                  <div className="flex gap-4 w-full justify-center">
                    {isPaused ? (
                      <Button variant="primary" onClick={() => actions.resumeFocus()} icon={FiPlay}>
                        Resume
                      </Button>
                    ) : (
                      <Button variant="secondary" onClick={() => actions.pauseFocus()} icon={FiPause}>
                        Pause
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={handleEndEarly}
                      className={`w-full max-w-xs ${isActive || isPaused ? 'text-crimson border-crimson/20 hover:bg-crimson/5' : ''}`}
                      icon={FiSquare}
                    >
                      {isActive || isPaused ? 'End Session Early' : 'Begin Focus'}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleStart}
                  disabled={demoActive}
                  size="lg"
                  className="w-full max-w-xs"
                  icon={FiPlay}
                >
                  Begin Focus
                </Button>
              )}
            </div>
          </Card>
        </div>

        {!sessionVisible && (
          <div className="lg:col-span-4 space-y-6">
            {requestedTask && !isControlsDisabled && (
              <Card variant="pine" className="p-6">
                <p className="text-xs font-bold uppercase tracking-wider text-paper/60">Selected task</p>
                <h3 className="mt-2 text-xl font-cormorant font-bold text-paper">{requestedTask.title}</h3>
                <p className="mt-1 text-sm text-paper/70">
                  {requestedTask.project || 'General'} · {requestedTask.estimatedMinutes || 25} min suggested
                </p>
              </Card>
            )}

            {requestedTaskId && !requestedTask && !isControlsDisabled && (
              <Card className="p-6">
                <p className="text-sm text-pine/60">
                  This task is unavailable. You can continue with an unlinked focus session.
                </p>
              </Card>
            )}

            <Card className={`p-6 ${isControlsDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
              <h3 className="text-lg font-cormorant font-bold text-pine mb-4">Duration</h3>
              <div className="grid grid-cols-2 gap-3">
                {focusLengths.map((length) => (
                  <button
                    key={length}
                    type="button"
                    onClick={() => actions.setFocusLength(length)}
                    disabled={isControlsDisabled}
                    aria-pressed={state.focus.length === length}
                    className={`py-3 rounded-xl font-dm-sans font-bold transition-all border
                      ${state.focus.length === length
                        ? 'bg-pine text-paper border-pine shadow-md'
                        : 'bg-white text-pine border-pine/10 hover:border-pine/30 hover:bg-pine/5'}
                      ${isControlsDisabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                  >
                    {length} <span className="text-xs font-normal opacity-70">min</span>
                  </button>
                ))}
              </div>
            </Card>

            <Card className={`p-6 ${isControlsDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
              <h3 className="text-lg font-cormorant font-bold text-pine mb-4">Select Scene</h3>
              <h4 className="mb-3 text-sm font-bold text-pine">Beyond the Garden</h4>
              <div className="grid grid-cols-2 gap-3">
                {scenes.map((scene, index) => {
                  const locked = !canUseScene(verifiedPlus ? 'plus' : 'free', scene.id);

                  return (
                    <React.Fragment key={scene.id}>
                      <div className="overflow-hidden rounded-xl border border-pine/10 bg-white">
                        <button
                          type="button"
                          onClick={() => handleSceneSelect(scene)}
                          disabled={isControlsDisabled}
                          className={`w-full text-left transition-all
                            ${selectedScene === scene.id && !demoActive
                              ? 'ring-2 ring-inset ring-pine'
                              : ''}
                            ${isControlsDisabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                          aria-pressed={selectedScene === scene.id && !demoActive}
                        >
                          <div
                            className="w-full aspect-[4/3]"
                            style={isImageScene(scene.id) ? { aspectRatio: '1 / 1' } : undefined}
                          >
                            <PaintingCanvas
                              id={`studio-scene-${scene.id}`}
                              scene={scene.id}
                              progress={1}
                              artSeed={5100 + index}
                              className="w-full h-full"
                            />
                          </div>
                          <span className="flex flex-col items-start gap-1 p-3">
                            <span className="font-dm-sans font-medium leading-snug text-pine">{scene.label}</span>
                            <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-pine/45">
                              {scene.tier === 'plus' ? 'Plus' : 'Free'}
                              {locked && <SafeIcon icon={FiLock} className="text-goldenrod" />}
                            </span>
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => startPreview(scene.id)}
                          disabled={isControlsDisabled}
                          className="w-full border-t border-pine/10 py-2 inline-flex items-center justify-center gap-1 text-xs font-bold text-pine/50 hover:text-pine disabled:opacity-50"
                          aria-label={`Preview ${scene.label}`}
                        >
                          <SafeIcon icon={FiEye} /> Preview 30 sec
                        </button>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </Card>

            <Card className={`p-6 ${isControlsDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
              <h3 className="text-lg font-cormorant font-bold text-pine mb-4">Intention (Optional)</h3>
              <input
                ref={intentionRef}
                type="text"
                placeholder="What will you focus on?"
                defaultValue={intention}
                onChange={(event) => setIntention(event.target.value)}
                disabled={isControlsDisabled}
                className={`w-full bg-white border border-pine/10 rounded-xl px-4 py-3 text-pine placeholder:text-pine/30 focus:outline-none focus:border-pine/30 focus:ring-1 focus:ring-pine/30 transition-all font-dm-sans ${isControlsDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              />
            </Card>
          </div>
        )}
      </div>

      {!sessionVisible && (
        <div className="pt-8">
          <h2 className="text-2xl font-cormorant font-bold text-pine mb-6 flex items-center gap-2">
            <SafeIcon icon={FiImage} className="text-sage" /> Your Gallery
          </h2>

          {verifiedPlus && (
            <Button variant="secondary" onClick={() => openCollectionModal()} icon={FiFolder} className="mb-6">
              New collection
            </Button>
          )}

          {exportError && (
            <div>
              <div className="text-crimson text-sm bg-crimson/10 px-4 py-3 rounded-xl inline-flex items-center gap-2 mb-6 border border-crimson/20">
                <SafeIcon icon={FiIcons.FiAlertCircle} /> {exportError}
              </div>
              {exportOriginalUrl && (
                <a
                  href={exportOriginalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mb-6 block text-sm font-bold text-crimson underline"
                >
                  Open original image
                </a>
              )}
            </div>
          )}

          {visibleGallery.length === 0 ? (
            <Card className="p-12 text-center bg-white/50 border-dashed border-pine/20 shadow-none">
              <div className="w-16 h-16 bg-pine/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiImage} className="w-8 h-8 text-pine/30" />
              </div>
              <h3 className="text-xl font-cormorant font-bold text-pine mb-2">A blank canvas</h3>
              <p className="text-pine/60 font-dm-sans max-w-md mx-auto">
                Your completed focus sessions will appear here as beautiful, unique pieces of art. Start a session to create your first masterpiece.
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleGallery.map((art) => (
                <Card key={art.id} className="p-4 overflow-hidden bg-white group">
                  <div
                    className="w-full aspect-[4/3] rounded-2xl overflow-hidden border border-pine/5 mb-4 bg-paper relative"
                    style={isImageScene(art.scene) ? { aspectRatio: '1 / 1' } : undefined}
                  >
                    <PaintingCanvas
                      id={`canvas-${art.id}`}
                      scene={art.scene}
                      progress={1}
                      artSeed={art.artSeed}
                      className="w-full h-full"
                    />
                  </div>
                  <div>
                    <h4 className="font-bold text-pine font-dm-sans">{getSceneName(art.scene)}</h4>
                    <p className="text-xs text-pine/50">
                      {new Date(art.completedAt).toLocaleDateString()} · {art.length}m focus
                    </p>
                    {art.taskTitle && (
                      <p className="text-xs text-pine/70 mt-1">
                        {art.taskTitle}{art.taskProject ? ` · ${art.taskProject}` : ''}
                      </p>
                    )}
                    {art.intention && <p className="text-xs text-pine/70 italic mt-1 line-clamp-1">"{art.intention}"</p>}
                    <div className="grid grid-cols-2 gap-2 mt-4">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          clearReadyExport();
                          setSelectedArt(art);
                        }}
                      >
                        View
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDownload(art)}
                        icon={FiDownload}
                        disabled={exportingId === `${art.id}-standard`}
                        aria-label={`Download ${art.scene} painting`}
                      >
                        {exportingId === `${art.id}-standard` ? 'Preparing...' : 'Export'}
                      </Button>
                    </div>
                    {readyExport?.artId === art.id && (
                      <div className="mt-3 grid gap-2">
                        <a
                          href={readyExport.url}
                          download={readyExport.filename}
                          className="min-h-11 w-full rounded-xl bg-pine px-4 py-2 inline-flex items-center justify-center text-sm font-bold text-paper focus:outline-none focus:ring-2 focus:ring-pine focus:ring-offset-2"
                        >
                          {readyExport.label}
                        </a>
                        <a
                          href={readyExport.url}
                          target="_blank"
                          rel="noreferrer"
                          className="min-h-11 w-full rounded-xl border border-pine/10 bg-white px-4 py-2 inline-flex items-center justify-center text-sm font-bold text-pine focus:outline-none focus:ring-2 focus:ring-pine"
                        >
                          Open PNG
                        </a>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {collections.length > 0 && (
            <div className="mt-8">
              <h3 className="mb-4 flex items-center gap-2 text-xl font-cormorant font-bold text-pine">
                <SafeIcon icon={FiFolder} /> Collections
              </h3>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {collections.map((collection) => {
                  const visibleCount = (collection.artIds || [])
                    .filter((artId) => visibleGalleryIds.has(artId)).length;

                  return (
                    <Card key={collection.id} className="p-4">
                      <p className="font-bold text-pine">{collection.name}</p>
                      <p className="mt-1 text-xs text-pine/50">{visibleCount} artworks</p>
                    </Card>
                  );
                })}
              </div>
              {!verifiedPlus && (
                <p className="mt-3 text-sm text-pine/60">
                  Existing collections remain viewable. Editing requires Studio Plus.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={Boolean(selectedArt)}
        onClose={() => {
          clearReadyExport();
          setSelectedArt(null);
        }}
        title={selectedArt ? getSceneName(selectedArt.scene) : 'Artwork'}
        size="lg"
      >
        {selectedArt && (
          <div className="p-6">
            <PaintingCanvas
              id={`modal-canvas-${selectedArt.id}`}
              scene={selectedArt.scene}
              progress={1}
              artSeed={selectedArt.artSeed}
              className="w-full aspect-[4/3] rounded-2xl border border-pine/10"
              style={isImageScene(selectedArt.scene) ? { aspectRatio: '1 / 1', maxWidth: '42rem', marginInline: 'auto' } : undefined}
            />
            <dl className="mt-5 grid gap-3 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-pine/50">Completed</dt>
                <dd className="font-bold text-pine">{new Date(selectedArt.completedAt).toLocaleDateString()}</dd>
              </div>
              <div>
                <dt className="text-pine/50">Focused</dt>
                <dd className="font-bold text-pine">{selectedArt.length || 0} minutes</dd>
              </div>
              <div>
                <dt className="text-pine/50">Task</dt>
                <dd className="font-bold text-pine">{selectedArt.taskTitle || selectedArt.intention || 'Unlinked focus'}</dd>
              </div>
            </dl>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button
                variant="secondary"
                onClick={() => handleDownload(selectedArt, false, `modal-canvas-${selectedArt.id}`)}
                icon={FiDownload}
                disabled={exportingId === `${selectedArt.id}-standard`}
                aria-label={`Download ${selectedArt.scene} painting`}
              >
                {exportingId === `${selectedArt.id}-standard` ? 'Preparing...' : 'Prepare standard PNG'}
              </Button>
              {!isImageScene(selectedArt.scene) && (
                <Button
                  variant="secondary"
                  onClick={() => handleDownload(selectedArt, true, `modal-canvas-${selectedArt.id}`)}
                  icon={verifiedPlus ? FiDownload : FiLock}
                  disabled={exportingId === `${selectedArt.id}-high`}
                  aria-label={`Download high-resolution ${selectedArt.scene} painting`}
                >
                  {exportingId === `${selectedArt.id}-high` ? 'Preparing...' : 'Prepare high-resolution PNG'}
                </Button>
              )}
              {verifiedPlus && (
                <Button variant="ghost" onClick={() => openCollectionModal(selectedArt)} icon={FiFolder}>
                  Add to collection
                </Button>
              )}
            </div>
            {readyExport?.artId === selectedArt.id && (
              <div className="mt-4 rounded-2xl border border-sage/30 bg-sage/10 p-4">
                <p className="text-sm font-bold text-pine">PNG ready</p>
                <div className="mt-3 flex flex-wrap gap-3">
                  <a
                    href={readyExport.url}
                    download={readyExport.filename}
                    className="min-h-11 rounded-xl bg-pine px-5 py-2 inline-flex items-center justify-center text-sm font-bold text-paper focus:outline-none focus:ring-2 focus:ring-pine focus:ring-offset-2"
                  >
                    {readyExport.label}
                  </a>
                  <a
                    href={readyExport.url}
                    target="_blank"
                    rel="noreferrer"
                    className="min-h-11 rounded-xl border border-pine/10 bg-white px-5 py-2 inline-flex items-center justify-center text-sm font-bold text-pine focus:outline-none focus:ring-2 focus:ring-pine"
                  >
                    Open image preview
                  </a>
                </div>
              </div>
            )}
            {exportError && exportOriginalUrl && (
              <div className="mt-4 rounded-xl border border-crimson/20 bg-crimson/10 p-4 text-sm text-crimson">
                <p>{exportError}</p>
                <a
                  href={exportOriginalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-block font-bold underline"
                >
                  Open original image
                </a>
              </div>
            )}
            {!verifiedPlus && (
              <p className="mt-3 text-xs text-pine/55">
                Standard export is free. <Link to="/membership" className="font-bold underline">Explore Studio Plus</Link> for high-resolution export.
              </p>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={collectionModalOpen}
        onClose={closeCollectionModal}
        title="Add to collection"
        size="sm"
      >
        <div className="p-6">
          {collectionArt?.id && collections.length > 0 && (
            <div className="mb-5 space-y-2">
              {collections.map((collection) => (
                <button
                  key={collection.id}
                  type="button"
                  onClick={() => addToCollection(collection.id, collectionArt.id)}
                  className="w-full rounded-xl border border-pine/10 px-4 py-3 text-left font-bold text-pine hover:bg-pine/5"
                >
                  {collection.name}
                </button>
              ))}
            </div>
          )}
          <form onSubmit={createCollection}>
            <label htmlFor="collection-name" className="text-sm font-bold text-pine">
              New collection name
            </label>
            <input
              id="collection-name"
              ref={collectionNameRef}
              type="text"
              autoComplete="off"
              className="mt-2 w-full rounded-xl border border-pine/10 bg-white px-4 py-3 text-pine focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
            />
            <div className="mt-4 flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={closeCollectionModal}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Create
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={showEndConfirm}
        onClose={() => setShowEndConfirm(false)}
        onConfirm={confirmEndEarly}
        title="End Session Early?"
        message="Your unfinished painting won’t be added to your gallery. You can return to focus anytime."
        confirmText="End session"
        cancelText="Keep focusing"
        variant="danger"
      />
    </div>
  );
}